<div class="dashboard-toolbar">
  @if ((isSelfServicePublished && !isSharedReadonlyView) || layoutEditEnabled) {
  <div class="layout-toolbar-actions">
    @if (isSelfServicePublished && !isSharedReadonlyView) {
    <button type="button" class="layout-cancel-action" (click)="backToSelfServiceBuilder()">
      ← Back to Builder
    </button>
    <button
      type="button"
      class="layout-share-action"
      matTooltip="Copy a read-only link others can view (but not edit)"
      (click)="shareThisDashboard()"
    >
      Share
    </button>
    }

    @if (layoutEditEnabled) {
      @if (!editMode) {
      <button type="button" class="layout-mode-toggle" (click)="enterEditMode()">
        Edit Layout
      </button>
      } @else {
      <div class="layout-save-actions">
        <button type="button" class="layout-cancel-action" (click)="cancelEditMode()">
          Cancel
        </button>
        <button type="button" class="layout-mode-toggle editing" (click)="saveMyLayout()">
          {{ layoutSavePersonalLabel }}
        </button>
        @if (canSaveDefaultLayout) {
        <button type="button" class="layout-default-action" (click)="saveDefaultLayout()">
          {{ layoutSaveDefaultLabel }}
        </button>
        }
      </div>
      }
    }
  </div>
  }
</div>

<!-- Dashboard body: global filter bar + main content. The filter bar's placement
     (top/bottom/left/right, from config.filters.position) is driven by
     data-filter-pos + CSS flex so a saved "left"/"right" position is honoured in
     the published view, not just "top". -->
<div class="dashboard-body-layout" [attr.data-filter-pos]="globalFiltersPosition">

<!-- Global filters: apply across every tab -->
@if (configurations?.filters) {
<div class="dashboard-global-filter-panel"
     [class.vertical]="globalFiltersPosition === 'left' || globalFiltersPosition === 'right'">
  <app-global-filter [config]="configurations?.filters"></app-global-filter>
</div>
}

<div class="dashboard-main-column">

<!-- Tab strip: only rendered for tabbed dashboards. Pill "segmented control":
     a rounded track holds the tabs; the active tab is a filled floating pill. -->
@if (hasTabs) {
<div class="dashboard-tabs-wrap">
  <nav class="dashboard-tabs" role="tablist" aria-label="Dashboard views">
    @for (tab of tabs; track tab.id) {
    <button
      type="button"
      role="tab"
      class="dashboard-tab"
      [class.active]="tab.id === activeTabId"
      [attr.aria-selected]="tab.id === activeTabId"
      (click)="switchTab(tab.id)"
    >
      {{ tab.label }}
    </button>
    }
  </nav>
</div>
}

<!-- Local filters: scoped to the active tab. The @for over the active tab is
     a single-item loop whose track key is the tab id — switching tabs changes
     the key, forcing Angular to destroy and re-create the filter component so
     it re-initializes with the new tab's filter config. -->
@if (activeTab && activeTabFilters) {
  @for (t of [activeTab]; track t.id) {
  <app-global-filter [config]="t.filters"></app-global-filter>
  }
}

<div #dashboardContent class="dashboard-content" [class.no-scroll-content]="!effectiveScreenFloating">
  <div #gridContainer class="dashboard-grid" [class.editing]="editMode" [class.interacting]="isInteracting"
    [class.has-focus]="hasFocusWidget" [class.ss-published]="isSelfServicePublished" [ngStyle]="gridStyle">

    @for (entry of viewItems; track entry.item.id) {
    <article class="grid-widget" [class.dragging]="isDragging(entry.item.id)"
      [class.resizing]="isResizing(entry.item.id)" [class.locked]="entry.item.locked" [class.editing]="editMode"
      [class.focused]="isFocused(entry.item.id)" [attr.data-widget-id]="entry.item.id"
      (pointerdown)="onWidgetPointerDown($event, entry.item)" [ngStyle]="widgetStyle(entry.item)">

      <!-- Recompute overlay — shown ONLY on the chart(s) this input control
           targets while its recompute POST is in flight, not the whole dashboard. -->
      @if (isWidgetRecomputing(entry.rec.widget)) {
      <div class="dvm-recompute-overlay">
        <span class="dvm-recompute-spinner" aria-hidden="true"></span>
      </div>
      }

      <!-- Widget Renderer with lazy loading -->
      @defer (on viewport) {
      <app-widget-renderer [charts]="entry.rec.widget">
      </app-widget-renderer>
      } @placeholder {
      <div style="min-height: 180px;"></div>
      }

      <!-- On-the-Fly Computation button (input/slider self-service widgets only) -->
      @if (canShowOtfButton(entry.rec.widget)) {
      <button type="button" class="widget-otf-btn" title="On-the-Fly Computation"
        (pointerdown)="$event.stopPropagation()" (click)="openWidgetOtf(entry.rec.widget, $event)">
        <span class="material-icons">bolt</span>
      </button>
      }

      <!-- Focus Mode Toggle -->
      @if (canShowFocusToggle(entry.rec.widget)) {
      <button type="button" class="focus-mode-toggle" [class.active]="isFocused(entry.item.id)" [attr.aria-label]="
              focusToggleLabel(entry.item.id, entry.rec.widget)
            " [matTooltip]="focusToggleLabel(entry.item.id, entry.rec.widget)"
        matTooltipClass="dvm-focus-toggle-tooltip"
        (pointerdown)="onFocusTogglePointerDown($event)" (click)="toggleFocus(entry.item.id)">
        <span class="material-icons">{{ focusToggleIcon(entry.item.id) }}</span>
      </button>
      }

      <!-- Resize Handles -->
      <button type="button" class="resize-handle resize-nw" [disabled]="!canResize(entry.item)"
        [attr.aria-label]="'Resize ' + label(entry.rec.widget)"
        (pointerdown)="startResize($event, entry.item, 'nw')"></button>

      <button type="button" class="resize-handle resize-ne" [disabled]="!canResize(entry.item)"
        [attr.aria-label]="'Resize ' + label(entry.rec.widget)"
        (pointerdown)="startResize($event, entry.item, 'ne')"></button>

      <button type="button" class="resize-handle resize-sw" [disabled]="!canResize(entry.item)"
        [attr.aria-label]="'Resize ' + label(entry.rec.widget)"
        (pointerdown)="startResize($event, entry.item, 'sw')"></button>

      <button type="button" class="resize-handle resize-se" [disabled]="!canResize(entry.item)"
        [attr.aria-label]="'Resize ' + label(entry.rec.widget)"
        (pointerdown)="startResize($event, entry.item, 'se')"></button>

    </article>
    }

  </div>
</div>
</div><!-- /.dashboard-main-column -->
</div><!-- /.dashboard-body-layout -->

<app-on-the-fly-computation
  [(visible)]="otfOpen"
  [compact]="true"
  [overrideColumns]="otfColumns"
  [overrideEntityName]="otfEntity"
  [baseRows]="otfRows"
  [widgetId]="otfWidget?.widgetId || otfWidget?.id || ''"
  (visibleChange)="$event || closeWidgetOtf()"
  (appliedRows)="onPublishedOtfRows($event)"
  (dataResult)="onPublishedOtfDataResult($event)"
  (computationApplied)="onPublishedOtfComputationApplied($event)"
></app-on-the-fly-computation>
